import { useCallback, useEffect, useRef, useState } from "react";

export const FIELD_PULL_THRESHOLD_PX = 64;
export const FIELD_PULL_MAX_PX = 88;

export function resistFieldPull(dy: number) {
  if (dy <= 0) return 0;
  return Math.min(FIELD_PULL_MAX_PX, dy * 0.42);
}

export function shouldTriggerFieldRefresh(displayedPull: number) {
  return displayedPull >= FIELD_PULL_THRESHOLD_PX;
}

export function isFieldPullIgnoredTarget(target: EventTarget | null) {
  return (
    target instanceof Element &&
    Boolean(target.closest("input, textarea, select, canvas, option"))
  );
}

export function defaultFieldPullIsAtTop(scroller: HTMLElement | null) {
  const nodeTop = scroller?.scrollTop ?? 0;
  const windowTop = typeof window === "undefined" ? 0 : window.scrollY;
  return nodeTop <= 0 && windowTop <= 0;
}

export function useOnFieldRefresh(refreshToken: number, onRefresh: () => void) {
  const skipFirst = useRef(true);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  useEffect(() => {
    if (skipFirst.current) {
      skipFirst.current = false;
      return;
    }
    onRefreshRef.current();
  }, [refreshToken]);
}

type PullToRefreshOptions = {
  onRefresh: () => void | Promise<void>;
  disabled?: boolean;
  isAtTop?: (scroller: HTMLElement | null) => boolean;
};

export function useFieldPullToRefresh({
  onRefresh,
  disabled = false,
  isAtTop = defaultFieldPullIsAtTop,
}: PullToRefreshOptions) {
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullDistanceRef = useRef(0);
  const trackingRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    pulling: boolean;
  } | null>(null);
  const onRefreshRef = useRef(onRefresh);
  const isAtTopRef = useRef(isAtTop);
  const disabledRef = useRef(disabled);
  const refreshingRef = useRef(refreshing);
  onRefreshRef.current = onRefresh;
  isAtTopRef.current = isAtTop;
  disabledRef.current = disabled;
  refreshingRef.current = refreshing;

  const resetPull = useCallback(() => {
    trackingRef.current = null;
    pullDistanceRef.current = 0;
    setPullDistance(0);
  }, []);

  useEffect(() => {
    if (!scroller) return;

    const updateDistance = (next: number) => {
      pullDistanceRef.current = next;
      setPullDistance(next);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (disabledRef.current || refreshingRef.current) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (isFieldPullIgnoredTarget(event.target)) return;
      if (!isAtTopRef.current(scroller)) return;
      trackingRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        pulling: false,
      };
    };

    const onPointerMove = (event: PointerEvent) => {
      const tracking = trackingRef.current;
      if (!tracking || tracking.pointerId !== event.pointerId) return;
      const dx = event.clientX - tracking.startX;
      const dy = event.clientY - tracking.startY;
      if (!tracking.pulling) {
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
          trackingRef.current = null;
          return;
        }
        if (dy < 12) return;
        if (!isAtTopRef.current(scroller)) {
          trackingRef.current = null;
          return;
        }
        tracking.pulling = true;
        if (scroller.hasPointerCapture?.(event.pointerId) !== true) {
          scroller.setPointerCapture?.(event.pointerId);
        }
      }
      if (!tracking.pulling) return;
      if (event.cancelable) event.preventDefault();
      updateDistance(resistFieldPull(dy));
    };

    const finish = async (event: PointerEvent) => {
      const tracking = trackingRef.current;
      if (!tracking || tracking.pointerId !== event.pointerId) return;
      const shouldRefresh =
        tracking.pulling && shouldTriggerFieldRefresh(pullDistanceRef.current);
      trackingRef.current = null;
      if (!shouldRefresh) {
        updateDistance(0);
        return;
      }
      refreshingRef.current = true;
      setRefreshing(true);
      updateDistance(FIELD_PULL_THRESHOLD_PX);
      try {
        await onRefreshRef.current();
      } finally {
        refreshingRef.current = false;
        setRefreshing(false);
        updateDistance(0);
      }
    };

    scroller.addEventListener("pointerdown", onPointerDown);
    scroller.addEventListener("pointermove", onPointerMove, { passive: false });
    scroller.addEventListener("pointerup", finish);
    scroller.addEventListener("pointercancel", finish);
    return () => {
      scroller.removeEventListener("pointerdown", onPointerDown);
      scroller.removeEventListener("pointermove", onPointerMove);
      scroller.removeEventListener("pointerup", finish);
      scroller.removeEventListener("pointercancel", finish);
    };
  }, [scroller]);

  useEffect(() => {
    if (disabled) resetPull();
  }, [disabled, resetPull]);

  return {
    scrollerRef: setScroller,
    pullDistance,
    refreshing,
  };
}
